import classnames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import {
    getBehavior,
    getFramework,
    getOperateDirection,
    getOperateType,
    MoveListenerLevel,
    useIdentity,
    useMoveEventListener,
    useScale,
} from '@/framework';
import { useResizeCursor } from '@/utils/cursor-kit';
import { ITouchKitEvent } from '@/utils/event-kit';
import {
    BaseLayerViewType,
    HIDDEN_OPERATE_MIN_HEIGHT,
    HIDDEN_OPERATE_MIN_WIDTH,
    ILayerProps,
    ResizeOperateType,
} from '@/version-types';
import { useResizeOperate } from '../hooks/use-resize-operate';

export const ResizeOperate: React.FC<{ layer: ILayerProps }> = ({ layer }) => {
    const identity = useIdentity();
    const framework = getFramework(identity);
    const [curOperateType, setCurOperateType] = useState<ResizeOperateType | null>(null);
    const { hiddenBtns, isSimpleBtn } = getBehavior(identity, layer)?.resizeOperateStyle?.() ?? {};
    const [showResizeWhenGroup, setShowResizeWhenGroup] = useState({
        top: true,
        bottom: true,
        left: true,
        right: true,
    });
    const resizeOperate = useResizeOperate();
    const resizeInfo = useRef<{
        id: string;
        direction: ResizeOperateType;
        operateType: BaseLayerViewType;
        cursor: string;
    } | null>(null);

    function checkCanResize(e: ITouchKitEvent) {
        if (framework.operate.isDisableResize()) return;
        const target = e.target;
        if (!target) return;
        const operateType = getOperateType(e.target);
        if (operateType !== BaseLayerViewType.RESIZE) {
            return;
        }
        const direction = getOperateDirection(e.target);
        const cursorKey = direction?.toUpperCase();
        return { id: layer.id, direction, cursor: cursor[cursorKey], operateType };
    }

    useEffect(() => {
        const resizeState = framework.checkCanResizeOperate(layer);
        setShowResizeWhenGroup(resizeState);
    }, [layer]);

    useMoveEventListener(
        (e) => {
            setCurOperateType(
                resizeInfo.current?.operateType === BaseLayerViewType.RESIZE
                    ? resizeInfo.current?.direction
                    : (BaseLayerViewType.MOVE as any),
            );
            if (e.isEnd) {
                setCurOperateType(null);
                if (resizeInfo.current) {
                    document.body.classList.remove(resizeInfo.current.cursor);
                }
            }
            if (!resizeInfo.current) {
                const res = checkCanResize(e);
                if (!res) return;
                resizeInfo.current = res;
            }
            if (e.isStart && resizeInfo.current) {
                resizeOperate.beforeResize(resizeInfo.current.id, resizeInfo.current.direction);
                if (resizeInfo.current.operateType === BaseLayerViewType.RESIZE) {
                    document.body.classList.add(resizeInfo.current.cursor);
                }
            }
        },
        MoveListenerLevel.PREPARE_LEVEL,
        'resize-pre',
    );

    useMoveEventListener(
        (e: ITouchKitEvent) => {
            if (!resizeInfo.current) return;
            resizeOperate.resize(resizeInfo.current.id, layer.rotate, resizeInfo.current.direction, e);
            if (e.isEnd) {
                resizeInfo.current = null;
            }
        },
        MoveListenerLevel.DEFAULT_LEVEL,
        'resize-default',
    );

    const { width, height } = layer;
    const cursor = useResizeCursor(layer.rotate);
    // const minHide = width <= HIDDEN_OPERATE_MIN_WIDTH || height <= HIDDEN_OPERATE_MIN_HEIGHT;
    const scale = useScale();
    const scaleWidth = width * scale;
    const scaleHeight = height * scale;
    const minHide = scaleWidth <= HIDDEN_OPERATE_MIN_WIDTH || scaleHeight <= HIDDEN_OPERATE_MIN_HEIGHT;

    const [isDisableResize, setIsDisableResize] = useState(framework.operate.isDisableResize());

    useEffect(() => {
        const handler = framework.events.onEditorOperateState((state) => {
            setIsDisableResize(state.disableResize);
        });

        return () => {
            framework.events.off(handler);
        };
    }, []);

    if (isDisableResize) return null;

    // console.log('ot resize', { minHide });

    return (
        <div className={classnames('active-operate__resize', { 'is-simple-btn': isSimpleBtn, mini: minHide })}>
            <div
                className={classnames({
                    'is-none': !showResizeWhenGroup.top && !showResizeWhenGroup.bottom,
                })}
            >
                {!hiddenBtns?.includes(ResizeOperateType.TC) && showResizeWhenGroup.top && (
                    <span
                        className={classnames('resize', ResizeOperateType.TC, cursor.TC, {
                            'is-none': minHide && curOperateType !== ResizeOperateType.TC,
                            'is-hidden': curOperateType && curOperateType !== ResizeOperateType.TC,
                        })}
                        data-operate-type={BaseLayerViewType.RESIZE}
                        data-direction={ResizeOperateType.TC}
                    />
                )}
                {!hiddenBtns?.includes(ResizeOperateType.BC) && showResizeWhenGroup.bottom && (
                    <span
                        className={classnames('resize', ResizeOperateType.BC, cursor.BC, {
                            'is-hidden': curOperateType && curOperateType !== ResizeOperateType.BC,
                        })}
                        data-operate-type={BaseLayerViewType.RESIZE}
                        data-direction={ResizeOperateType.BC}
                    />
                )}
            </div>
            <div
                className={classnames({
                    'is-none': !showResizeWhenGroup.left && !showResizeWhenGroup.right,
                })}
            >
                {!hiddenBtns?.includes(ResizeOperateType.LC) && showResizeWhenGroup.left && (
                    <span
                        className={classnames('resize', ResizeOperateType.LC, cursor.LC, {
                            'is-none': minHide && curOperateType !== ResizeOperateType.LC,
                            'is-hidden': curOperateType && curOperateType !== ResizeOperateType.LC,
                        })}
                        data-operate-type={BaseLayerViewType.RESIZE}
                        data-direction={ResizeOperateType.LC}
                    />
                )}
                {!hiddenBtns?.includes(ResizeOperateType.RC) && showResizeWhenGroup.right && (
                    <span
                        className={classnames('resize', ResizeOperateType.RC, cursor.RC, {
                            'is-hidden': curOperateType && curOperateType !== ResizeOperateType.RC,
                        })}
                        data-operate-type={BaseLayerViewType.RESIZE}
                        data-direction={ResizeOperateType.RC}
                    />
                )}
            </div>
        </div>
    );
};
